import { connect } from "react-redux";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  FormControl,
  MenuItem,
  Avatar,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  Rating,
  FormControlLabel,
  IconButton,
  Chip,
} from "@mui/material";
import DisplayReview from "../seller/reviews/DisplayReview";
import TuneIcon from "@mui/icons-material/Tune";
import StarIcon from "@mui/icons-material/Star";
import { useEffect, useState } from "react";
import SeeAllReviews from "../../pages/stores/storedetails/SeeAllReviews";
import ClearIcon from "@mui/icons-material/Clear";
import { Swiper, SwiperSlide } from "swiper/react";
import config from "../../config";
import SwiperCore, { Autoplay, Navigation } from "swiper";
import "swiper/css";
import "swiper/css/navigation";

const ReviewCard = ({ data, onClick }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: "#1a1a1a",
        border: "1px solid #2a2a2a",
        borderRadius: "12px",
        p: 2.5,
        cursor: "pointer",
        transition: "all 0.2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        "&:hover": {
          border: "1px solid #FFC001",
          backgroundColor: "#1e1e1e",
        },
      }}
    >
      {/* Top: avatar + name + date */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          src={data.user.avatar_url}
          alt={data.user.full_name}
          sx={{ width: 44, height: 44, border: "2px solid #2a2a2a" }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ color: "#fff", fontWeight: 700, fontSize: "14px" }}>
            {data.user.full_name}
          </Typography>
          <Typography sx={{ color: "#555", fontSize: "11px" }}>
            {formatDate(data.created_at)}
          </Typography>
        </Box>
      </Box>

      {/* Stars */}
      <Rating
        value={data.rating}
        readOnly
        size="small"
        sx={{ "& .MuiRating-iconFilled": { color: "#FFC001" } }}
      />

      {/* Content */}
      <Typography
        sx={{
          color: "#aaa",
          fontSize: "13px",
          lineHeight: 1.7,
          flex: 1,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical",
        }}
      >
        {data.content}
      </Typography>

      {data.content.length > 140 && (
        <Typography sx={{ color: "#FFC001", fontSize: "12px", fontWeight: 600 }}>
          Read more →
        </Typography>
      )}
    </Box>
  );
};

const Reviews = ({ reviews, jwtToken, listData, reviewType }) => {
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [selectedValue, setSelectedValue] = useState("Newest");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("");
  const [review, setReview] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [completeReview, setCompleteReview] = useState();
  const [openDialog, setOpenDialog] = useState(false);

  SwiperCore.use([Autoplay]);

  useEffect(() => {
    setReview(reviews);
  }, [reviews]);

  const handleRadioChange = (event) => setSelectedFilter(event.target.value);
  const handleResetClick = () => setSelectedFilter("all");
  const handleSeeMore = (data) => { setCompleteReview(data); setShowReview(true); };
  const handleCloseReviewModal = () => setShowReview(false);

  const handleApplyFilter = async () => {
    setOpenFilterDialog(false);
    let url;
    if (selectedFilter === "all" && reviewType === "animal") {
      url = `${config.SERVER_URL}/api/v1/reviews?seller_id=${listData.seller_id}`;
    } else if (reviewType === "animal") {
      url = `${config.SERVER_URL}/api/v1/reviews?rating=${selectedFilter}&seller_id=${listData?.seller?.id}`;
    } else if (selectedFilter === "all") {
      url = `${config.SERVER_URL}/api/v1/reviews?listing_service_id=${listData.id}`;
    } else {
      url = `${config.SERVER_URL}/api/v1/reviews?rating=${selectedFilter}&listing_service_id=${listData.id}`;
    }
    try {
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${jwtToken}` } });
      if (res.status === 200) { setReview(res.data.reviews); setSelectedFilter("all"); }
    } catch (err) {}
  };

  const handleSortChange = async (event) => {
    const sortValue = event.target.value;
    setSelectedValue(sortValue);
    let order = sortValue === "Newest" ? "asc" : sortValue === "Oldest" ? "desc" : "default";
    setSortOrder(order);
    const url = reviewType === "animal"
      ? `${config.SERVER_URL}/api/v1/reviews?sort_by=${order}&seller_id=${listData.seller.id}`
      : `${config.SERVER_URL}/api/v1/reviews?sort_by=${order}&listing_service_id=${listData.id}`;
    try {
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${jwtToken}` } });
      if (res.status === 200) setReview(res.data.reviews);
    } catch (err) {}
  };

  // Average rating
  const avgRating = review.length > 0
    ? (review.reduce((sum, r) => sum + r.rating, 0) / review.length).toFixed(1)
    : null;

  return (
    <>
      {completeReview && (
        <DisplayReview openModal={showReview} closeModal={handleCloseReviewModal} data={completeReview} />
      )}
      {review.length > 0 && (
        <SeeAllReviews data={review} openDialog={openDialog} setOpenDialog={setOpenDialog} />
      )}

      {/* Filter dialog */}
      <Dialog
        open={openFilterDialog}
        onClose={() => setOpenFilterDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a1a",
            border: "1px solid #2a2a2a",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "16px" }}>Filter Reviews</Typography>
          <IconButton onClick={() => setOpenFilterDialog(false)} sx={{ color: "#555" }}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography sx={{ color: "#ccc", fontWeight: 600, fontSize: "14px" }}>Rating</Typography>
            <Button onClick={handleResetClick} sx={{ color: "#FFC001", fontSize: "12px", textTransform: "none" }}>
              Reset
            </Button>
          </Box>
          <RadioGroup value={selectedFilter} onChange={handleRadioChange}>
            {["all", "5 stars", "4 stars", "3 stars", "2 stars", "1 stars"].map((v) => (
              <FormControlLabel
                key={v}
                value={v}
                control={<Radio sx={{ color: "#444", "&.Mui-checked": { color: "#FFC001" } }} />}
                label={<Typography sx={{ color: "#ccc", fontSize: "14px" }}>{v === "all" ? "All" : v}</Typography>}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleApplyFilter}
            fullWidth
            sx={{
              backgroundColor: "#FFC001",
              color: "#000",
              fontWeight: 700,
              fontSize: "14px",
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": { backgroundColor: "#e6ac00" },
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Section header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <StarIcon sx={{ color: "#FFC001", fontSize: 22 }} />
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: { xs: "18px", md: "22px" } }}>
          Seller{" "}
          <Box component="span" sx={{ color: "#FFC001" }}>Reviews</Box>
        </Typography>
        {review.length > 0 && (
          <Chip
            label={review.length}
            size="small"
            sx={{ backgroundColor: "#222", color: "#777", fontWeight: 700, fontSize: "11px", height: "20px" }}
          />
        )}
      </Box>

      {review.length > 0 ? (
        <>
          {/* Summary + controls row */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
            {avgRating && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ color: "#FFC001", fontWeight: 800, fontSize: "36px", lineHeight: 1 }}>
                  {avgRating}
                </Typography>
                <Box>
                  <Rating
                    value={parseFloat(avgRating)}
                    readOnly
                    precision={0.1}
                    size="small"
                    sx={{ "& .MuiRating-iconFilled": { color: "#FFC001" } }}
                  />
                  <Typography sx={{ color: "#555", fontSize: "12px" }}>
                    {review.length} {review.length === 1 ? "review" : "reviews"}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <FormControl size="small">
                <Select
                  value={selectedValue}
                  onChange={handleSortChange}
                  sx={{
                    height: "34px",
                    color: "#ccc",
                    fontSize: "13px",
                    backgroundColor: "#1a1a1a",
                    border: "1px solid #2a2a2a",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "& .MuiSvgIcon-root": { color: "#555" },
                  }}
                >
                  <MenuItem value="Newest">Newest</MenuItem>
                  <MenuItem value="Oldest">Oldest</MenuItem>
                  <MenuItem value="Default">Default</MenuItem>
                </Select>
              </FormControl>
              <Button
                startIcon={<TuneIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => setOpenFilterDialog(true)}
                sx={{
                  height: "34px",
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #2a2a2a",
                  color: "#ccc",
                  fontSize: "13px",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2,
                  "&:hover": { borderColor: "#FFC001", color: "#FFC001" },
                }}
              >
                Filter
              </Button>
            </Box>
          </Box>

          {/* Reviews carousel / grid */}
          {review.length > 3 ? (
            <>
              <Box sx={{ "& .swiper": { pb: 1 } }}>
                <Swiper
                  style={{ "--swiper-navigation-color": "#FFC001" }}
                  navigation={true}
                  modules={[Navigation]}
                  grabCursor={true}
                  loop={review.length > 4}
                  spaceBetween={16}
                  breakpoints={{
                    320: { slidesPerView: 1 },
                    600: { slidesPerView: 2 },
                    900: { slidesPerView: 3 },
                    1200: { slidesPerView: 4 },
                  }}
                >
                  {review.map((data, i) => (
                    <SwiperSlide key={i} style={{ height: "auto" }}>
                      <ReviewCard data={data} onClick={() => handleSeeMore(data)} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  onClick={() => setOpenDialog(true)}
                  sx={{
                    backgroundColor: "transparent",
                    border: "1px solid #2a2a2a",
                    color: "#ccc",
                    borderRadius: "20px",
                    textTransform: "none",
                    px: 3,
                    fontSize: "13px",
                    "&:hover": { borderColor: "#FFC001", color: "#FFC001" },
                  }}
                >
                  See All {review.length} Reviews
                </Button>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: `repeat(${review.length}, 1fr)` },
              }}
            >
              {review.map((data, i) => (
                <ReviewCard key={i} data={data} onClick={() => handleSeeMore(data)} />
              ))}
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            backgroundColor: "#1a1a1a",
            border: "1px solid #2a2a2a",
            borderRadius: "12px",
            py: 5,
            textAlign: "center",
          }}
        >
          <StarIcon sx={{ color: "#2a2a2a", fontSize: 40, mb: 1 }} />
          <Typography sx={{ color: "#555", fontSize: "14px" }}>No reviews yet</Typography>
        </Box>
      )}
    </>
  );
};

const mapStateToProps = ({ Auth }) => ({ jwtToken: Auth.jwtToken });
const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Reviews);
